import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import BrandDialog, {
  BrandDialogVariant,
} from '../components/ui/BrandDialog'

type AlertOptions = {
  title?: string
  message: string
  variant?: BrandDialogVariant
  confirmLabel?: string
}

type ConfirmOptions = {
  title?: string
  message: string
  variant?: BrandDialogVariant
  confirmLabel?: string
  cancelLabel?: string
}

type DialogState = {
  open: boolean
  mode: 'alert' | 'confirm'
  title: string
  message: string
  variant: BrandDialogVariant
  confirmLabel: string
  cancelLabel: string
}

type DialogApi = {
  alert: (messageOrOptions: string | AlertOptions) => Promise<void>
  confirm: (messageOrOptions: string | ConfirmOptions) => Promise<boolean>
}

const DialogContext = createContext<DialogApi | null>(null)

type Resolver =
  | { mode: 'alert'; resolve: () => void }
  | { mode: 'confirm'; resolve: (value: boolean) => void }

let externalApi: DialogApi | null = null

/** Imperative helpers — safe to call outside React components after DialogProvider mounts. */
export async function brandAlert(
  messageOrOptions: string | AlertOptions
): Promise<void> {
  if (!externalApi) {
    window.alert(
      typeof messageOrOptions === 'string'
        ? messageOrOptions
        : messageOrOptions.message
    )
    return
  }
  return externalApi.alert(messageOrOptions)
}

export async function brandConfirm(
  messageOrOptions: string | ConfirmOptions
): Promise<boolean> {
  if (!externalApi) {
    return window.confirm(
      typeof messageOrOptions === 'string'
        ? messageOrOptions
        : messageOrOptions.message
    )
  }
  return externalApi.confirm(messageOrOptions)
}

const defaultState: DialogState = {
  open: false,
  mode: 'alert',
  title: '',
  message: '',
  variant: 'info',
  confirmLabel: 'OK',
  cancelLabel: 'Cancel',
}

function normalizeAlert(input: string | AlertOptions): Required<AlertOptions> {
  if (typeof input === 'string') {
    return {
      title: 'Fit by Cassie',
      message: input,
      variant: 'info',
      confirmLabel: 'OK',
    }
  }
  return {
    title: input.title || 'Fit by Cassie',
    message: input.message,
    variant: input.variant || 'info',
    confirmLabel: input.confirmLabel || 'OK',
  }
}

function normalizeConfirm(
  input: string | ConfirmOptions
): Required<ConfirmOptions> {
  if (typeof input === 'string') {
    return {
      title: 'Please confirm',
      message: input,
      variant: 'confirm',
      confirmLabel: 'Confirm',
      cancelLabel: 'Cancel',
    }
  }
  return {
    title: input.title || 'Please confirm',
    message: input.message,
    variant: input.variant || 'confirm',
    confirmLabel: input.confirmLabel || 'Confirm',
    cancelLabel: input.cancelLabel || 'Cancel',
  }
}

export const DialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [dialog, setDialog] = useState<DialogState>(defaultState)
  const resolverRef = useRef<Resolver | null>(null)

  const close = useCallback((confirmed: boolean) => {
    const resolver = resolverRef.current
    resolverRef.current = null
    setDialog((prev) => ({ ...prev, open: false }))
    if (!resolver) return
    if (resolver.mode === 'alert') resolver.resolve()
    else resolver.resolve(confirmed)
  }, [])

  const alert = useCallback((messageOrOptions: string | AlertOptions) => {
    const opts = normalizeAlert(messageOrOptions)
    return new Promise<void>((resolve) => {
      resolverRef.current = { mode: 'alert', resolve }
      setDialog({
        open: true,
        mode: 'alert',
        title: opts.title,
        message: opts.message,
        variant: opts.variant,
        confirmLabel: opts.confirmLabel,
        cancelLabel: 'Cancel',
      })
    })
  }, [])

  const confirm = useCallback((messageOrOptions: string | ConfirmOptions) => {
    const opts = normalizeConfirm(messageOrOptions)
    return new Promise<boolean>((resolve) => {
      resolverRef.current = { mode: 'confirm', resolve }
      setDialog({
        open: true,
        mode: 'confirm',
        title: opts.title,
        message: opts.message,
        variant: opts.variant,
        confirmLabel: opts.confirmLabel,
        cancelLabel: opts.cancelLabel,
      })
    })
  }, [])

  const api = useMemo(() => ({ alert, confirm }), [alert, confirm])
  externalApi = api

  return (
    <DialogContext.Provider value={api}>
      {children}
      <BrandDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        variant={dialog.variant}
        confirmLabel={dialog.confirmLabel}
        cancelLabel={dialog.cancelLabel}
        showCancel={dialog.mode === 'confirm'}
        onConfirm={() => close(true)}
        onCancel={() => close(false)}
      />
    </DialogContext.Provider>
  )
}

export function useDialog() {
  const ctx = useContext(DialogContext)
  if (!ctx) {
    throw new Error('useDialog must be used within DialogProvider')
  }
  return ctx
}
